import React from "react";
import { Icons } from "../Icons";

interface ErrorStateProps {
  message: string;
  title?: string;
  icon?: React.ReactNode;
  variant?: "inline" | "card" | "banner" | "minimal";
  size?: "sm" | "md" | "lg";
  className?: string;
  onRetry?: () => void;
  retryText?: string;
  centered?: boolean;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  message,
  title = "Erro",
  icon,
  variant = "card",
  size = "md",
  className = "",
  onRetry,
  retryText = "Tentar novamente",
  centered = true,
}) => {
  const sizeClasses = {
    sm: {
      icon: "text-lg",
      title: "text-sm font-medium",
      message: "text-xs",
      padding: "p-3",
    },
    md: {
      icon: "text-xl",
      title: "text-base font-semibold",
      message: "text-sm",
      padding: "p-4",
    },
    lg: {
      icon: "text-2xl",
      title: "text-lg font-bold",
      message: "text-base",
      padding: "p-6",
    },
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "inline":
        return "bg-red-50 border border-red-200 text-red-800 rounded-lg";
      case "banner":
        return "bg-red-50 border-l-4 border-red-500 text-red-800 rounded-r-lg";
      case "minimal":
        return "text-red-600";
      case "card":
      default:
        return "bg-red-50 border border-red-200 text-red-800 rounded-xl shadow-sm";
    }
  };

  const containerClasses = `
    ${getVariantClasses()}
    ${sizeClasses[size].padding}
    ${centered ? "flex flex-col items-center justify-center text-center" : ""}
    animate-fade-in
    ${className}
  `.trim();

  const iconNode = icon ?? <Icons.Warning sx={{ fontSize: size === "sm" ? 20 : size === "lg" ? 28 : 24 }} />;
  if (variant === "minimal") {
    return (
      <div className={`${containerClasses} ${centered ? "text-center" : ""}`}>
        <span className={`${sizeClasses[size].icon} mr-2 flex items-center justify-center`}>{iconNode}</span>
        <span className={sizeClasses[size].message}>{message}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="ml-2 text-red-700 underline hover:text-red-900 text-sm"
          >
            {retryText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div
        className={`flex ${
          centered ? "flex-col items-center" : "items-start"
        } gap-3`}
      >
        <div className={`${centered ? "text-center" : ""} flex-1`}>
          {title && (
            <h4 className={`${sizeClasses[size].title} mb-1`}>{title}</h4>
          )}
          <p className={`${sizeClasses[size].message} leading-relaxed`}>
            {message}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              {retryText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
